import { Button } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, Loader2, Pencil } from "lucide-react";
import { useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useLeaveRequestDetailQuery } from "../api/queries";
import {
  LeaveRequestDetailTour,
  useLeaveRequestDetailTour,
} from "../tour/LeaveRequestDetailTour";
import { LeaveRequestForm } from "../ui/LeaveRequestForm";

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const APPROVALS_SEARCH = {
  offset: 0,
  limit: 20,
  sort: undefined as { field: string; dir: "asc" | "desc" }[] | undefined,
  periodYear: currentYear,
  periodMonth: currentMonth,
  periodFromOnly: true,
  filterByCreated: true,
  status: undefined as string | undefined,
  search: undefined as string | undefined,
};

type DetailSearch = {
  from?: string;
  offset?: number;
  limit?: number;
  sort?: { field: string; dir: "asc" | "desc" }[];
  periodYear?: number;
  periodMonth?: number;
  periodFromOnly?: boolean;
  filterByCreated?: boolean;
  status?: string;
  search?: string;
};

export function LeaveRequestDetailPage() {
  const nav = useNavigate();
  const { id } = useParams({ strict: false });
  const tour = useLeaveRequestDetailTour();
  const detailSearch = useSearch({ strict: false }) as DetailSearch | undefined;
  const detailQuery = useLeaveRequestDetailQuery(id);
  const request = detailQuery.data;

  const fromHistory = detailSearch?.from === "history";
  const fromCreateFlow = detailSearch?.from === "create";
  const backSearch = detailSearch
    ? {
        offset: detailSearch.offset ?? 0,
        limit: detailSearch.limit ?? 20,
        sort: detailSearch.sort as
          | { field: string; dir: "asc" | "desc" }[]
          | undefined,
        periodYear: detailSearch.periodYear ?? currentYear,
        periodMonth: detailSearch.periodMonth ?? currentMonth,
        periodFromOnly: detailSearch.periodFromOnly !== false,
        filterByCreated: detailSearch.filterByCreated !== false,
        status: detailSearch.status,
        search: detailSearch.search,
      }
    : APPROVALS_SEARCH;

  const handleBack = () => {
    if (fromCreateFlow) {
      nav({ to: "/app/hr/leave/requests/new" });
      return;
    }
    if (fromHistory) {
      nav({ to: "/app/hr/leave/history", search: backSearch });
    } else {
      nav({ to: "/app/hr/leave/approvals", search: backSearch });
    }
  };

  const editSearch = useMemo(() => {
    if (!detailSearch) {
      return {
        from: undefined as string | undefined,
        offset: 0,
        limit: 20,
        periodYear: currentYear,
        periodMonth: currentMonth,
        status: undefined as string | undefined,
        search: undefined as string | undefined,
      };
    }
    return {
      from: detailSearch.from,
      offset: detailSearch.offset ?? 0,
      limit: detailSearch.limit ?? 20,
      periodYear: detailSearch.periodYear ?? currentYear,
      periodMonth: detailSearch.periodMonth ?? currentMonth,
      status: detailSearch.status,
      search: detailSearch.search,
    };
  }, [detailSearch]);

  const handleEdit = () => {
    nav({
      to: "/app/hr/leave/requests/$id/edit",
      params: { id: id ?? "" },
      search: editSearch,
    });
  };

  if (detailQuery.isLoading || !request) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <LeaveRequestDetailTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between gap-2"
          data-tourid="leave-request-detail-back"
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">ລາຍລະອຽດຄຳຂໍລາ</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">
              {request.leaveTypeName} — {request.startDate.slice(0, 10)} ຫາ{" "}
              {request.endDate.slice(0, 10)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              ກັບຄືນ
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleEdit}
              className="gap-2"
            >
              <Pencil className="h-4 w-4" />
              ແກ້ໄຂ
            </Button>
          </div>
        </div>

        <div
          className="mx-auto w-full max-w-2xl space-y-6"
          data-tourid="leave-request-detail-form"
        >
          <LeaveRequestForm
            mode="detail"
            request={request}
            onCancel={handleBack}
            onEdit={handleEdit}
          />
        </div>
      </Main>
    </>
  );
}
